import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useRsi } from '../../hooks/useRsi';
import { Table } from '../Table';
import type { Data, Field, Fields, Meta } from '../../types';
import { MULTI_VALUE_SEPARATOR } from '../../types';
import { matchOptionValue, normalizeForMatch, normalizeDateSeparators, sanitizeNumericText } from '../../cellValues';
import type { CalculatedColumn, DataGridHandle, FillEvent, RowHeightArgs, RowsChangeData } from 'react-data-grid';
import { addErrorsAndRunHooks, buildBlankRows } from '../dataMutations';
import { ROW_NUMBER_COLUMN_KEY, generateColumns } from '../ValidationStepColumns';
import Button from 'common/components/button/Button';
import toggleSwitchStyle from 'common/components/toggleSwitch/ToggleSwitch.module.scss'
import { useTranslation } from 'react-i18next';
import { FaCheckDouble, FaPlus, FaRedo, FaTrashAlt, FaUndo } from 'react-icons/fa';
import { useToasts } from 'react-toast-notifications';
import { Columns } from './MatchColumns';
import FileSelector from 'common/components/fileSelector/FileSelector';
import Label from 'common/components/label/Label';
import CustomFile from 'common/models/CustomFile';
import { Col, Row } from 'react-flexbox-grid';
import ReactTooltip from 'react-tooltip';
import { Item, Menu, Separator, useContextMenu, theme } from 'react-contexify';
import 'react-contexify/dist/ReactContexify.min.css';

type Props<T extends string> = {
  initialData: Data<T>[];
  matchedOptions: any[];
  matchedColumns: Columns<T>;
  file?: File;
  onPrevious: () => Promise<void>;
};

// Task #66347: quantas linhas em branco o botão "+" acrescenta de cada vez.
const ADD_ROWS_STEP = 10;

// Task #66347: quantos passos o desfazer/refazer guarda. Cada passo é um array de ponteiros para as
// linhas — as que não mudaram são partilhadas entre estados —, por isso cinquenta não pesam nada.
const MAX_HISTORY = 50;

// Task #66347: alturas da linha — a expandida tem de dar para as etiquetas de uma célula multi-valor.
const ROW_HEIGHT = 35;
const EXPANDED_ROW_HEIGHT = 92;

// Task #66347: ações de linha disponíveis no menu de contexto da grelha.
const ROW_ACTIONS_MENU_ID = 'spreadsheet-row-actions';
type RowAction = 'insertAbove' | 'insertBelow' | 'duplicate' | 'copy' | 'cut' | 'paste' | 'delete';

// Task #66347: botão direito no cabeçalho — ações sobre a grelha toda (levar tudo para o Excel).
const COPY_ALL_MENU_ID = 'spreadsheet-copy-all';

// Task #66347: botão direito sobre uma célula — só copiar e colar. As ações de linha ficaram de fora
// de propósito: com uma coluna de custos copiada do Excel, o "colar linha" despejava-os a partir da
// primeira coluna (a matrícula) em vez de os pôr onde o cursor estava.
const CELL_ACTIONS_MENU_ID = 'spreadsheet-cell-actions';

// Task #66347: bloco de células escolhido com o rato. As posições são as que se veem (linha visível +
// coluna), por isso deixam de valer assim que as linhas mudam.
const CELL_RANGE_CLASS = 'rdg-cell-range';

type CellPosition = { rowIdx: number; idx: number };
type CellRange = { anchor: CellPosition; focus: CellPosition };

const boundsOf = (range: CellRange | null) => (range ? {
  fromRow: Math.min(range.anchor.rowIdx, range.focus.rowIdx),
  toRow: Math.max(range.anchor.rowIdx, range.focus.rowIdx),
  fromIdx: Math.min(range.anchor.idx, range.focus.idx),
  toIdx: Math.max(range.anchor.idx, range.focus.idx),
} : null);

const isInsideBounds = (bounds: ReturnType<typeof boundsOf>, position: CellPosition) => !!bounds
  && position.rowIdx >= bounds.fromRow && position.rowIdx <= bounds.toRow
  && position.idx >= bounds.fromIdx && position.idx <= bounds.toIdx;

// Task #66347: escreve no clipboard do sistema. O caminho moderno é assíncrono e exige contexto
// seguro; o textarea + execCommand fica como rede para os casos em que não está disponível.
const writeClipboardText = async (text: string) => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (error) {
    // sem permissão ou fora de contexto seguro — tenta o caminho antigo
  }

  try {
    const area = document.createElement('textarea');
    area.value = text;
    area.style.position = 'fixed';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    const copied = document.execCommand('copy');
    document.body.removeChild(area);
    return copied;
  } catch (error) {
    return false;
  }
};

// Task #66347: lê o clipboard do sistema. Só o Ctrl+V traz o conteúdo no próprio evento; a partir de
// um clique num menu é preciso pedi-lo, o que pode ser recusado (permissão, contexto não seguro) e
// devolve então vazio, para quem chama tratar disso.
const readClipboardText = async () => {
  try {
    return (await navigator.clipboard?.readText()) ?? '';
  } catch (error) {
    return '';
  }
};

// Task #66347: uma linha s\u00f3 conta quando tem alguma coisa. As linhas em branco com que a grelha
// arranca n\u00e3o s\u00e3o erro nenhum \u2014 s\u00e3o espa\u00e7o por preencher.
// Task #66347: comparações de conteúdo — servem para não substituir o estado dos avisos por outro
// igual, que só forçava trabalho de render sem nada de novo para mostrar.
const sameKeys = (a: Set<string>, b: Set<string>) =>
  a.size === b.size && Array.from(a).every(key => b.has(key));

const sameWarnings = (a: Map<string, string[]>, b: Map<string, string[]>) =>
  a.size === b.size && Array.from(a).every(([key, messages]) => {
    const other = b.get(key);
    return !!other && other.length === messages.length && other.every((message, index) => message === messages[index]);
  });

const isRowBlank = <T extends string>(row: Data<T>, fields: Fields<T>) =>
  fields.every(field => {
    const value = row[field.key as T];
    return value === undefined || value === null || value === '' || value === false;
  });

export const ValidationStep = <T extends string>({ initialData, file, onPrevious, matchedOptions, matchedColumns }: Props<T>) => {
  const { fields, onClose, onSubmit, rowHook, tableHook, allowInvalidSubmit, isCellEditable, getCellLockedReason, asyncRowValidation, showRowNumber,
    initialFilterByErrors, showAttachments, enablePaste, enableFillHandle, allowAddRows, ignoreBlankRows, renderGridFooter, renderToolbarActions, enableHistory,
    submitButtonText, validationMessage, startWithBlankRows, onBack, asPage, enableRowActions,
    onUploadFile, getFileName } = useRsi<T>();
  const { t } = useTranslation();

  // Task #66347: com ignoreBlankRows, uma linha ainda por preencher não fica pintada de vermelho —
  // caso contrário a grelha em branco abria com todas as células obrigatórias em erro.
  const validate = useCallback((rows: (Data<T> & Partial<Meta>)[]) => {
    const validated = addErrorsAndRunHooks<T>(rows, fields, rowHook, tableHook);
    if (!ignoreBlankRows) return validated;
    return validated.map(row => (isRowBlank(row, fields) ? { ...row, __errors: null } : row));
  }, [fields, rowHook, tableHook, ignoreBlankRows]);

  const [data, setData] = useState<(Data<T> & Meta)[]>(
    useMemo(() => validate(initialData), []),
  );
  // Task #66347: desfazer/refazer. Todas as alterações da grelha passam pelo `updateData`, por isso é
  // lá que se guarda o estado anterior — um funil só, sem cada ação ter de se lembrar do histórico.
  const [undoStack, setUndoStack] = useState<(Data<T> & Meta)[][]>([]);
  const [redoStack, setRedoStack] = useState<(Data<T> & Meta)[][]>([]);
  // O estado atual sem o tornar dependência: o `updateData` é usado por meia dúzia de callbacks e tem
  // de se manter estável.
  const dataRef = useRef(data);
  dataRef.current = data;
  const [selectedRows, setSelectedRows] = useState<ReadonlySet<number | string>>(new Set());
  const [filterByErrors, setFilterByErrors] = useState(initialFilterByErrors);
  const [showSubmitAlert, setShowSubmitAlert] = useState(false);
  const { addToast } = useToasts();
  const warningToast = useCallback((description: string) => { addToast(description, { appearance: 'warning' }) }, []);

  const [chargeAttachments, setChargeAttachments] = useState<CustomFile[]>([]);

  // Managed container for ReactTooltip portal — created once, cleaned up on unmount
  const tooltipContainerRef = useRef<HTMLDivElement | null>(null);
  if (!tooltipContainerRef.current) {
    tooltipContainerRef.current = document.createElement('div');
    tooltipContainerRef.current.setAttribute('data-tooltip-container', 'validation');
    document.body.appendChild(tooltipContainerRef.current);
  }
  useEffect(() => {
    return () => {
      if (tooltipContainerRef.current?.parentNode) {
        tooltipContainerRef.current.parentNode.removeChild(tooltipContainerRef.current);
        tooltipContainerRef.current = null;
      }
    };
  }, []);

  // `recordHistory` a falso para as alterações que não são uma ação do utilizador (ver a validação
  // assíncrona): contadas como passo próprio, o primeiro Ctrl+Z desfazia-as a elas em vez de desfazer
  // o que a pessoa tinha acabado de fazer.
  const updateData = useCallback(
    (rows: typeof data, recordHistory = true) => {
      if (enableHistory && recordHistory) {
        // O estado anterior lê-se já, e não dentro do updater: o updater corre quando o React quiser,
        // e a ref entretanto podia estar a apontar a outra coisa.
        const previous = dataRef.current;
        setUndoStack(stack => [...stack, previous].slice(-MAX_HISTORY));
        // Uma alteração nova fecha a porta ao refazer, como em toda a parte.
        setRedoStack(stack => (stack.length ? [] : stack));
      }
      setData(validate(rows));
    },
    [setData, validate, enableHistory],
  );

  const undo = useCallback(() => {
    const previous = undoStack[undoStack.length - 1];
    if (!previous) return;

    const current = dataRef.current;
    setUndoStack(stack => stack.slice(0, -1));
    setRedoStack(stack => [...stack, current].slice(-MAX_HISTORY));
    // O estado guardado já passou pela validação — repõe-se tal e qual.
    setData(previous);
  }, [undoStack]);

  const redo = useCallback(() => {
    const next = redoStack[redoStack.length - 1];
    if (!next) return;

    const current = dataRef.current;
    setRedoStack(stack => stack.slice(0, -1));
    setUndoStack(stack => [...stack, current].slice(-MAX_HISTORY));
    setData(next);
  }, [redoStack]);

  // Os atalhos vivem na página e não na grelha: depois de carregar num botão da barra o foco está
  // noutro sítio, e um Ctrl+Z que não faz nada lê-se como avaria.
  const undoRef = useRef(undo);
  undoRef.current = undo;
  const redoRef = useRef(redo);
  redoRef.current = redo;

  useEffect(() => {
    if (!enableHistory) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (!event.ctrlKey && !event.metaKey) return;

      const target = event.target as HTMLElement | null;
      // A escrever numa célula (ou noutro campo), o desfazer é do próprio campo de texto.
      if (target?.closest?.('input, textarea, [contenteditable="true"]')) return;
      if (document.body.classList.contains('ReactModal__Body--open')) return;

      const key = event.key.toLowerCase();
      if (key === 'z' && !event.shiftKey) {
        event.preventDefault();
        undoRef.current();
        return;
      }

      // Ctrl+Y é o refazer do Windows; Ctrl+Shift+Z o dos browsers e do Mac. Valem os dois, senão
      // quem tem o outro hábito fica a olhar para uma grelha que não mexe.
      if (key === 'y' || (key === 'z' && event.shiftKey)) {
        event.preventDefault();
        redoRef.current();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [enableHistory]);

  const deleteSelectedRows = () => {
    if (selectedRows.size) {
      const newData = data.filter(value => !selectedRows.has(value.__index));
      updateData(newData);
      setSelectedRows(new Set());
    }
  };

  // Task #66347: ações que substituem linhas (replicar) deixam o __index anterior para trás — a linha
  // modelo desaparece e as novas nascem com __index próprio. Sem limpar os ids que já não existem, o
  // contador dizia mais linhas do que as marcadas e o replicar recusava-se a correr por achar que
  // havia várias selecionadas.
  useEffect(() => {
    setSelectedRows(current => {
      if (!current.size) return current;
      const alive = new Set<string>(data.map(row => row.__index));
      const kept = Array.from(current).filter(id => alive.has(String(id)));
      return kept.length === current.size ? current : new Set(kept);
    });
  }, [data]);

  const updateRow = useCallback(
    (rows: typeof data, changedData?: RowsChangeData<(typeof data)[number]>) => {
      const changes = changedData?.indexes.reduce(
        (acc, index) => {
          // when data is filtered val !== actual index in data
          const realIndex = data.findIndex(value => value.__index === rows[index].__index);
          acc[realIndex] = rows[index];
          return acc;
        },
        {} as Record<number, (typeof data)[number]>,
      );
      const newData = Object.assign([], data, changes);
      updateData(newData);

    },
    [data, updateData],
  );

  // Task #66078: best-effort inline warnings from server-side validation.
  // Task #66220: guardados pelo __index estável da linha e não pela ordem — com a ordem, apagar uma
  // linha fazia os avisos das seguintes passarem a apontar para a linha errada.
  const [warningsByRow, setWarningsByRow] = useState<Map<string, string[]>>(new Map());
  // Task #66347: linhas cujos avisos são todos informativos — pintam-se com outro tom, porque
  // vermelho num "este contrato já existe e vai ser atualizado" lê-se como erro.
  const [infoRows, setInfoRows] = useState<Set<string>>(new Set());
  // Task #66347: linhas expandidas (só faz sentido havendo colunas multi-valor, ver hasMultiValueColumn).
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const asyncRunRef = useRef(0);
  const asyncRowValidationRef = useRef(asyncRowValidation);
  asyncRowValidationRef.current = asyncRowValidation;

  // Task #66347: que preenchimento automático já foi aplicado a cada linha (__index → valuesKey).
  const appliedFillsRef = useRef<Map<string, string>>(new Map());
  // O efeito da validação só depende dos dados; a escrita de volta passa por uma ref para não o
  // recriar a cada render (recriá-lo cancelaria a validação a meio).
  const updateDataRef = useRef(updateData);
  updateDataRef.current = updateData;

  // Stable 1-based line for each row (by its unique __index), independent of the error filter.
  const lineByIndex = useMemo(() => {
    const map = new Map<string, number>();
    data.forEach((row, i) => map.set(row.__index, i + 1));
    return map;
  }, [data]);

  // Refs let the "#" column formatter read the latest line/warnings without rebuilding columns.
  const lineByIndexRef = useRef(lineByIndex);
  lineByIndexRef.current = lineByIndex;
  const warningsByRowRef = useRef(warningsByRow);
  warningsByRowRef.current = warningsByRow;
  const infoRowsRef = useRef(infoRows);
  infoRowsRef.current = infoRows;
  const expandedRowsRef = useRef(expandedRows);
  expandedRowsRef.current = expandedRows;

  const getRowLine = useCallback((row: Data<T> & Meta) => lineByIndexRef.current.get(row.__index), []);
  const getRowWarnings = useCallback((row: Data<T> & Meta) => warningsByRowRef.current.get(row.__index), []);
  const getRowIsInfo = useCallback((row: Data<T> & Meta) => infoRowsRef.current.has(row.__index), []);

  // A expansão só se justifica quando há uma célula que não cabe fechada.
  const hasMultiValueColumn = useMemo(() => fields.some(field => field.fieldType.type === 'multiselect'), [fields]);

  // Expandir só faz sentido quando há mesmo coisas escondidas: fechada, a célula mostra a primeira
  // etiqueta e um "+N". Com zero ou uma, a seta não revelava nada e só enchia a coluna de ruído.
  const canExpandRow = useCallback((row: Data<T> & Meta) => fields.some(field => {
    if (field.fieldType.type !== 'multiselect') return false;
    const value = (row[field.key as T] as string) || '';
    return value.split(MULTI_VALUE_SEPARATOR).filter(Boolean).length > 1;
  }), [fields]);
  const isRowExpanded = useCallback((row: Data<T> & Meta) => expandedRowsRef.current.has(row.__index), []);
  const toggleRowExpanded = useCallback((row: Data<T> & Meta) => {
    setExpandedRows(current => {
      const next = new Set(current);
      if (!next.delete(row.__index)) next.add(row.__index);
      return next;
    });
  }, []);
  // O RowHeightArgs do react-data-grid também cobre linhas de agrupamento (que aqui não existem).
  const rowHeight = useCallback((args: RowHeightArgs<Data<T> & Meta>) =>
    ('__index' in args.row && expandedRows.has((args.row as Meta).__index) ? EXPANDED_ROW_HEIGHT : ROW_HEIGHT), [expandedRows]);

  // Run the opt-in async validation on mount and (debounced) after edits; ignore stale responses.
  // Task #66220: a revalidação corre a cada alteração dos dados (editar uma célula ou apagar linhas) e
  // os avisos são limpos de imediato — qualquer alteração pode invalidar avisos de outras linhas (a
  // regra dos quilómetros compara cada linha com as restantes), e mostrar o resultado da corrida
  // anterior enquanto se revalida dava a ideia de que nada tinha mudado. O runId é incrementado já
  // aqui, para uma resposta que venha a caminho de dados antigos não voltar a pintar avisos
  // desatualizados.
  useEffect(() => {
    if (!asyncRowValidationRef.current) return;
    const rows = data;
    const runId = ++asyncRunRef.current;
    setWarningsByRow(current => (current.size > 0 ? new Map() : current));
    setInfoRows(current => (current.size > 0 ? new Set<string>() : current));

    const timer = setTimeout(() => {
      asyncRowValidationRef.current!(rows as Data<T>[])
        .then(results => {
          if (runId !== asyncRunRef.current) return; // stale response, the data changed meanwhile
          const map = new Map<string, string[]>();
          const infoOnly = new Set<string>();
          // Task #66347: valores conhecidos a preencher (ver `values` em asyncRowValidation).
          const filled: (Data<T> & Meta)[] = [...rows];
          let hasFills = false;

          (results ?? []).forEach(r => {
            // O `line` é 1-based sobre as linhas enviadas nesta corrida.
            const row = rows[r.line - 1];
            if (!row) return;

            if (r?.warnings?.length) {
              map.set(row.__index, r.warnings.map(w => w.message));
              if (r.warnings.every(w => w.level === 'info')) {
                infoOnly.add(row.__index);
              }
            }

            // Uma vez por linha e por registo correspondente: se o utilizador apagar depois um campo,
            // não voltamos a escrevê-lo por cima.
            if (r.values && r.valuesKey && appliedFillsRef.current.get(row.__index) !== r.valuesKey) {
              appliedFillsRef.current.set(row.__index, r.valuesKey);
              const patch: Record<string, unknown> = {};
              Object.entries(r.values).forEach(([key, value]) => {
                const current = row[key as T];
                // Só preenche o que está vazio — nunca substitui o que já foi escrito.
                if ((current === undefined || current === null || current === '') && value !== undefined && value !== '') {
                  patch[key] = value;
                }
              });
              if (Object.keys(patch).length) {
                filled[r.line - 1] = { ...row, ...patch } as Data<T> & Meta;
                hasFills = true;
              }
            }
          });

          // Task #66347: só troca o estado quando os avisos mudam mesmo. Um Map novo com o mesmo
          // conteúdo reconstruía as colunas e o react-data-grid remontava as células — o que fechava
          // o menu de uma célula multi-valor a cada resposta da validação.
          setWarningsByRow(current => (sameWarnings(current, map) ? current : map));
          setInfoRows(current => (sameKeys(current, infoOnly) ? current : infoOnly));
          // Não é uma ação do utilizador: fica agarrado à alteração que o provocou (ver updateData).
          if (hasFills) updateDataRef.current(filled, false);
        })
        .catch(() => { /* inline warnings are best-effort — never throw */ });
    }, 400);
    return () => clearTimeout(timer);
    // Só os dados disparam a revalidação: o callback vive numa ref para um render do ecrã que a
    // recrie (é uma arrow inline) não cancelar uma validação a meio nem limpar os avisos sem razão.
  }, [data]);

  const rowClass = useCallback((row: Data<T> & Meta) => {
    if (!asyncRowValidation || !warningsByRow.has(row.__index)) return undefined;
    return infoRows.has(row.__index) ? 'rdg-row-info' : 'rdg-row-warning';
  }, [asyncRowValidation, warningsByRow, infoRows]);

  const unmappedKeys = useMemo(() => {
    // Task #66347: "não mapeou dados" é conversa do passo de mapear colunas de um ficheiro. Numa
    // grelha que arranca em branco não há mapeamento nenhum, e o aviso aparecia em todas as colunas.
    if (startWithBlankRows) return new Set<T>();

    const mappedKeys = new Set(
      matchedColumns
        .filter(col => 'value' in col)
        .map(col => ('value' in col ? col.value : '') as T),
    );
    return new Set(fields.filter(f => !mappedKeys.has(f.key as T)).map(f => f.key as T));
  }, [fields, matchedColumns, startWithBlankRows]);

  const dateFormats = useMemo(() => {
    const formats = new Map<string, string>();
    matchedOptions.forEach((opt: any) => {
      if (opt.column === 'date' || opt.column === 'paymentDate') {
        formats.set(opt.column, opt.value);
      }
    });
    return formats;
  }, [matchedOptions]);

  const columns = useMemo(
    () => generateColumns(
      fields,
      unmappedKeys,
      t('spreadsheet_import.validation_step.unmapped_column_tooltip') as string,
      dateFormats,
      format => t('spreadsheet_import.validation_step.date_format_tooltip', { format }) as string,
      isCellEditable,
      getCellLockedReason,
      showRowNumber,
      getRowLine,
      getRowWarnings,
      getRowIsInfo,
      isRowExpanded,
      hasMultiValueColumn ? toggleRowExpanded : undefined,
      canExpandRow,
      onUploadFile,
      getFileName,
      t('spreadsheet_import.validation_step.attach_file') as string,
      t('spreadsheet_import.validation_step.remove_file') as string,
      t('spreadsheet_import.validation_step.row_actions_tooltip') as string,
    ),
    // Task #66078: warningsByRow na dependência força o rebuild das colunas quando a revalidação
    // muda os avisos → a célula "#" re-renderiza e o triângulo aparece/desaparece sem ser preciso clicar.
    [fields, unmappedKeys, dateFormats, isCellEditable, getCellLockedReason, showRowNumber, getRowLine, getRowWarnings, getRowIsInfo, isRowExpanded, toggleRowExpanded, canExpandRow, hasMultiValueColumn, warningsByRow, infoRows, expandedRows, onUploadFile, getFileName, t],
  );

  useEffect(() => {
    const timer = setTimeout(() => ReactTooltip.rebuild(), 50);
    return () => clearTimeout(timer);
  });

  const tableData = useMemo(() => {
    let result = [];
    if (filterByErrors) {
      result = data.filter(value => {
        if (value?.__errors) {
          return Object.values(value.__errors)?.filter(err => err.level === 'error').length;
        }
        return false;
      });
    } else {
      result = data;
    }

    return result;

  }, [data, filterByErrors]);


  // O menu de contexto resolve a linha pelo índice que o DOM da grelha expõe — que é sobre as linhas
  // visíveis, não sobre `data` (podem estar filtradas).
  const tableDataRef = useRef(tableData);
  tableDataRef.current = tableData;

  // Task #66347: o mesmo botão marca e desmarca — estando tudo marcado, a única coisa que faz sentido
  // a seguir é limpar a seleção. Conta-se sobre as linhas que se veem, que são as que ele marca (com
  // o filtro dos erros ligado, as escondidas não entram).
  const allRowsSelected = tableData.length > 0 && tableData.every(row => selectedRows.has(row.__index));

  const toggleSelectAllRows = useCallback(() => {
    setSelectedRows(allRowsSelected ? new Set() : new Set(tableData.map(row => row.__index)));
  }, [allRowsSelected, tableData]);

  // Task #66347: arrastar o canto da célula replica o valor pelas linhas por onde passa (fill handle
  // do Excel). O grid desenha o quadradinho sozinho a partir do momento em que recebe o onFill.
  const onFill = useCallback(({ columnKey, sourceRow, targetRow }: FillEvent<Data<T> & Meta>) => {
    const key = columnKey as T;
    // Respeita as células bloqueadas por regra de negócio (ex: periodicidade num contrato de compra).
    if (isCellEditable && !isCellEditable(targetRow, key)) return targetRow;
    return { ...targetRow, [key]: sourceRow[key] };
  }, [isCellEditable]);

  // Task #66347: primeiro clique seleciona a célula, segundo é que abre a edição — como no Excel.
  // O grid não tem esse modo (ou abre logo ao clique, ou só ao duplo/Enter), por isso guarda-se a
  // última célula clicada e, repetindo-se, pede-se-lhe para abrir o editor.
  const gridRef = useRef<DataGridHandle>(null);
  const lastClickedCellRef = useRef<{ rowIdx: number; idx: number; rowKey: string } | null>(null);

  const onCellClick = useCallback((row: Data<T> & Meta, column: CalculatedColumn<Data<T> & Meta>) => {
    const rowIdx = tableDataRef.current.findIndex(value => value.__index === row.__index);
    if (rowIdx < 0) return;

    const previous = lastClickedCellRef.current;
    lastClickedCellRef.current = { rowIdx, idx: column.idx, rowKey: row.__index };

    if (previous && previous.rowIdx === rowIdx && previous.idx === column.idx) {
      gridRef.current?.selectCell({ rowIdx, idx: column.idx }, true);
    }
  }, []);

  const rowKeyGetter = useCallback((row: Data<T> & Meta) => row.__index, []);

  // Task #66347: acrescentar linhas em branco ao fundo da grelha (grelha que arranca sem ficheiro).
  const addBlankRows = useCallback(() => {
    updateData([...data, ...buildBlankRows(fields, ADD_ROWS_STEP)] as (Data<T> & Meta)[]);
  }, [data, fields, updateData]);

  // Task #66347: colar blocos de células vindos do Excel. O `onPaste` do react-data-grid desta versão
  // só dispara depois de uma cópia feita dentro da própria grelha, por isso tratamos o evento nativo.
  // A âncora é a célula selecionada — o grid marca-a com aria-selected e expõe aria-colindex/rowindex.
  const gridWrapperRef = useRef<HTMLDivElement | null>(null);

  // Task #66347: seleção de um bloco de células, como no Excel: arrastar com o rato (ou shift+clique)
  // marca o retângulo e é esse retângulo que o Ctrl+C leva para o Excel — escolher cinco custos em
  // coluna e copiá-los não era possível. O react-data-grid desta versão só sabe selecionar uma célula,
  // por isso o bloco vive aqui. Fica numa ref e o realce é pintado à mão nas células: guiá-lo por
  // estado reconstruía as colunas a cada movimento do rato, e reconstruir colunas remonta a grelha
  // inteira.
  const cellRangeRef = useRef<CellRange | null>(null);
  const isSelectingRangeRef = useRef(false);

  // A primeira coluna de dados — as anteriores ("#" e visto de seleção) não entram no bloco.
  const firstDataColumnIndex = useMemo(() => {
    const fieldKeys = new Set(fields.map(field => field.key as string));
    const index = columns.findIndex(column => fieldKeys.has(column.key));
    return index < 0 ? 0 : index;
  }, [columns, fields]);

  const positionOfCell = useCallback((element: HTMLElement | null): CellPosition | null => {
    const cell = element?.closest?.('[role="gridcell"]') as HTMLElement | null;
    if (!cell) return null;

    const idx = Number(cell.getAttribute('aria-colindex')) - 1;
    const rowIdx = Number(cell.closest('[role="row"]')?.getAttribute('aria-rowindex')) - 2; // 1 = cabeçalho
    if (!(idx >= 0) || !(rowIdx >= 0) || !tableDataRef.current[rowIdx]) return null;

    return { rowIdx, idx };
  }, []);

  const paintCellRange = useCallback(() => {
    const wrapper = gridWrapperRef.current;
    if (!wrapper) return;

    const bounds = boundsOf(cellRangeRef.current);
    // Uma célula sozinha já leva o contorno do próprio grid — voltar a marcá-la era só ruído.
    const paint = !!bounds && (bounds.fromRow !== bounds.toRow || bounds.fromIdx !== bounds.toIdx);

    wrapper.querySelectorAll('[role="gridcell"]').forEach(element => {
      const cell = element as HTMLElement;
      const idx = Number(cell.getAttribute('aria-colindex')) - 1;
      const rowIdx = Number(cell.closest('[role="row"]')?.getAttribute('aria-rowindex')) - 2;
      cell.classList.toggle(CELL_RANGE_CLASS, paint && isInsideBounds(bounds, { rowIdx, idx }));
    });
  }, []);

  const clearCellRange = useCallback(() => {
    if (!cellRangeRef.current) return;
    cellRangeRef.current = null;
    paintCellRange();
  }, [paintCellRange]);

  // Mudando as linhas que se veem — editar, colar, apagar, ou o filtro dos erros, que reordena sem
  // mexer nos dados —, as posições guardadas deixam de apontar às mesmas células. Corre antes do
  // efeito que pinta (a ordem é a da escrita), para a marca sair no mesmo frame.
  useLayoutEffect(() => { cellRangeRef.current = null; }, [tableData]);

  // O grid só desenha as linhas visíveis: as células que entram no ecrã com o scroll (ou com um render
  // novo) nascem sem a marca, daí repô-la a seguir a cada render e a cada scroll.
  useLayoutEffect(() => { paintCellRange(); });

  useEffect(() => {
    const grid = gridWrapperRef.current?.querySelector('.rdg');
    if (!grid) return;

    let frame = 0;
    const onScroll = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(paintCellRange);
    };

    grid.addEventListener('scroll', onScroll);
    return () => { window.cancelAnimationFrame(frame); grid.removeEventListener('scroll', onScroll); };
  }, [paintCellRange]);

  useEffect(() => {
    const stopSelecting = () => {
      if (!isSelectingRangeRef.current) return;
      isSelectingRangeRef.current = false;
      if (gridWrapperRef.current) gridWrapperRef.current.style.userSelect = '';
    };

    document.addEventListener('mouseup', stopSelecting);
    return () => document.removeEventListener('mouseup', stopSelecting);
  }, []);

  const onGridMouseDown = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    if (!enablePaste || event.button !== 0) return;

    const target = event.target as HTMLElement;
    // O quadradinho de preenchimento e os editores têm o seu próprio arrasto.
    if (target.closest?.('input, textarea, [contenteditable="true"], .rdg-cell-drag-handle')) return;

    const position = positionOfCell(target);
    if (!position || position.idx < firstDataColumnIndex) {
      clearCellRange();
      return;
    }

    if (event.shiftKey && cellRangeRef.current) {
      // Shift+clique estica o bloco a partir da célula onde ele começou — para trinta linhas é bem
      // mais prático do que arrastar. O preventDefault é para o browser não ir selecionando o texto
      // pelo meio.
      event.preventDefault();
      cellRangeRef.current = { anchor: cellRangeRef.current.anchor, focus: position };
    } else {
      cellRangeRef.current = { anchor: position, focus: position };
    }

    isSelectingRangeRef.current = true;
    if (gridWrapperRef.current) gridWrapperRef.current.style.userSelect = 'none';

    // Arrastar de uma célula para outra não produz clique nenhum (o `click` nasce no ancestral comum
    // das duas), por isso o grid não chegava a selecionar nem a focar a célula de partida — e sem o
    // foco dentro da grelha o Ctrl+V/Ctrl+C seguinte nem chegava aqui. A célula de partida é também a
    // âncora do colar, tal como no Excel.
    gridRef.current?.selectCell(position);
    paintCellRange();
  }, [enablePaste, firstDataColumnIndex, positionOfCell, paintCellRange, clearCellRange]);

  const onGridMouseOver = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    if (!isSelectingRangeRef.current) return;

    const current = cellRangeRef.current;
    const position = positionOfCell(event.target as HTMLElement);
    if (!current || !position || position.idx < firstDataColumnIndex) return;
    if (current.focus.rowIdx === position.rowIdx && current.focus.idx === position.idx) return;

    cellRangeRef.current = { anchor: current.anchor, focus: position };
    paintCellRange();
  }, [firstDataColumnIndex, positionOfCell, paintCellRange]);

  const resolvePastedValue = useCallback((field: Field<T>, raw: string): string | boolean => {
    const text = (raw ?? '').replace(/^"|"$/g, '').trim();
    if (!text) return '';

    switch (field.fieldType.type) {
      case 'select':
        return matchOptionValue(field.fieldType.options, text);
      case 'multiselect':
        return text.split(/[;,]/).map(part => part.trim()).filter(Boolean)
          .map(part => matchOptionValue(field.fieldType.type === 'multiselect' ? field.fieldType.options : [], part))
          .join(MULTI_VALUE_SEPARATOR);
      case 'checkbox':
        return ['1', 'TRUE', 'SIM', 'YES', 'OUI', 'X'].includes(normalizeForMatch(text));
      case 'date':
        // Task #66347: o Excel do cliente traz as datas com o separador da folha de origem
        // (31.07.2026). Só se trocam os separadores por barras — adivinhar a ordem dos campos daria
        // dias e meses trocados sem ninguém dar por isso.
        return normalizeDateSeparators(text);
      default:
        // Task #66347: numa coluna de números, o que vem do Excel traz símbolo da moeda e separadores
        // de milhares — fica só o número, escrito como no resto da aplicação.
        return field.fieldType.type === 'input' && field.fieldType.numeric
          ? sanitizeNumericText(text, field.fieldType.numeric)
          : text;
    }
  }, []);

  // Âncora do colar a partir de uma posição da grelha (linha visível + coluna, contando com as
  // colunas do "#" e do visto de seleção, que não são dados).
  const anchorFromPosition = useCallback((rowIndex: number, columnIndex: number) => {
    const row = tableDataRef.current[rowIndex];
    if (!row) return null;

    // Estando o cursor numa das colunas que não são dados, cola-se a partir da primeira coluna de
    // dados dessa linha em vez de desistir: clicar no número da linha e colar é o gesto natural para
    // preencher a linha toda.
    const fieldIndex = fields.findIndex(field => field.key === columns[columnIndex]?.key);

    return { fieldIndex: fieldIndex < 0 ? 0 : fieldIndex, row };
  }, [columns, fields]);

  const findPasteAnchor = useCallback(() => {
    const cell = gridWrapperRef.current?.querySelector('[role="gridcell"][aria-selected="true"]');
    if (cell) {
      const columnIndex = Number(cell.getAttribute('aria-colindex')) - 1;
      const rowIndex = Number(cell.closest('[role="row"]')?.getAttribute('aria-rowindex')) - 2; // 1 = cabeçalho
      const anchor = anchorFromPosition(rowIndex, columnIndex);
      if (anchor) return anchor;
    }

    // Task #66347: o grid tira a marca à célula assim que o foco sai da grelha — basta um clique num
    // botão da barra ou o fecho de um menu para o Ctrl+V seguinte deixar de ter âncora e não fazer
    // nada. A última célula em que se clicou serve de rede.
    const last = lastClickedCellRef.current;
    if (!last) return null;

    // A linha resolve-se pelo seu __index e não pela posição de então: entre o clique e a colagem podem
    // ter desaparecido linhas acima, e colar na posição antiga era escrever por cima da linha errada
    // sem ninguém dar por isso. Deixando de existir, não há âncora nenhuma.
    const rowIndex = tableDataRef.current.findIndex(row => row.__index === last.rowKey);
    return rowIndex < 0 ? null : anchorFromPosition(rowIndex, last.idx);
  }, [anchorFromPosition]);

  // Task #66347: o caminho inverso do colar — o que sai tem de ser o que o `resolvePastedValue` sabe
  // voltar a ler, por isso vão etiquetas (e não ids), multi-valor separado por `;` e Sim/Não nos
  // checkboxes. Tabs e quebras de linha dentro de um valor partiriam a grelha do Excel.
  const cellToText = useCallback((row: Data<T> & Meta, field: Field<T>) => {
    const raw = row[field.key as T];
    let text: unknown = raw;

    switch (field.fieldType.type) {
      case 'checkbox':
        text = raw ? t('common.yes') : t('common.no');
        break;
      case 'select':
        text = field.fieldType.options.find(option => option.value === raw)?.label ?? raw ?? '';
        break;
      case 'multiselect': {
        const options = field.fieldType.options;
        text = String(raw ?? '').split(MULTI_VALUE_SEPARATOR)
          .map(part => part.trim()).filter(Boolean)
          .map(value => options.find(option => option.value === value)?.label ?? value)
          .join('; ');
        break;
      }
    }

    return String(text ?? '').replace(/[\t\r\n]+/g, ' ');
  }, [t]);

  const rowsToTsv = useCallback((rows: (Data<T> & Meta)[], withHeader: boolean) => {
    const lines = withHeader ? [fields.map(field => field.label).join('\t')] : [];
    rows.forEach(row => lines.push(fields.map(field => cellToText(row, field as unknown as Field<T>)).join('\t')));
    return lines.join('\n');
  }, [fields, cellToText]);

  // Task #66347: o bloco de células escolhido, no formato que o Excel espera — uma linha de texto por
  // linha da grelha, colunas separadas por tabulação.
  const cellRangeToTsv = useCallback(() => {
    const bounds = boundsOf(cellRangeRef.current);
    if (!bounds) return null;

    const rangeFields: Field<T>[] = [];
    for (let idx = bounds.fromIdx; idx <= bounds.toIdx; idx += 1) {
      const field = fields.find(item => item.key === columns[idx]?.key);
      if (field) rangeFields.push(field as unknown as Field<T>);
    }
    if (!rangeFields.length) return null;

    const lines: string[] = [];
    for (let rowIdx = bounds.fromRow; rowIdx <= bounds.toRow; rowIdx += 1) {
      const row = tableDataRef.current[rowIdx];
      if (row) lines.push(rangeFields.map(field => cellToText(row, field)).join('\t'));
    }

    return lines.length ? lines.join('\n') : null;
  }, [columns, fields, cellToText]);

  // Task #66347: apagar o conteúdo do bloco (o Ctrl+X é copiar + limpar). Vale o mesmo que no colar:
  // ficheiros são referências e células bloqueadas por regra de negócio ficam de fora.
  const blankCellRange = useCallback(() => {
    const bounds = boundsOf(cellRangeRef.current);
    if (!bounds) return;

    const next = [...data] as (Data<T> & Partial<Meta>)[];
    let changed = false;

    for (let rowIdx = bounds.fromRow; rowIdx <= bounds.toRow; rowIdx += 1) {
      const visible = tableDataRef.current[rowIdx];
      if (!visible) continue;

      const target = next.findIndex(row => row.__index === visible.__index);
      if (target < 0) continue;

      const row = { ...next[target] };
      for (let idx = bounds.fromIdx; idx <= bounds.toIdx; idx += 1) {
        const field = fields.find(item => item.key === columns[idx]?.key);
        if (!field || field.fieldType.type === 'file') continue;

        const key = field.key as T;
        if (isCellEditable && !isCellEditable(row as Data<T> & Meta, key)) continue;

        (row as Record<string, unknown>)[key] = field.fieldType.type === 'checkbox' ? false : '';
        changed = true;
      }
      next[target] = row;
    }

    if (changed) updateData(next as (Data<T> & Meta)[]);
  }, [data, fields, columns, isCellEditable, updateData]);

  // Task #66347: o coração do colar, partilhado pelo Ctrl+V e pelo "colar linha" do menu de contexto
  // — as duas vias têm de fazer exatamente o mesmo, senão o utilizador nunca sabe o que esperar.
  const applyPastedText = useCallback((text: string, anchor: { fieldIndex: number; row: Data<T> & Meta }) => {
    const matrix = text.replace(/\r\n?/g, '\n').replace(/\n+$/, '').split('\n').map(line => line.split('\t'));
    if (!matrix.length) return;

    const startRowIndex = data.findIndex(row => row.__index === anchor.row.__index);
    if (startRowIndex < 0) return;

    const newData = [...data] as (Data<T> & Partial<Meta>)[];
    matrix.forEach((cells, rowOffset) => {
      const targetIndex = startRowIndex + rowOffset;
      if (targetIndex >= newData.length) {
        newData.push(buildBlankRows(fields, 1)[0]);
      }
      const row = { ...newData[targetIndex] };
      cells.forEach((raw, columnOffset) => {
        const field = fields[anchor.fieldIndex + columnOffset];
        if (!field) return;
        const key = field.key as T;
        // Uma célula de ficheiro guarda uma referência, não texto: colar por cima só lá punha lixo.
        if (field.fieldType.type === 'file') return;
        // Respeita as células bloqueadas por regra de negócio (ex: periodicidade num contrato de compra).
        if (isCellEditable && !isCellEditable(row as Data<T> & Meta, key)) return;
        (row as Record<string, unknown>)[key] = resolvePastedValue(field as unknown as Field<T>, raw);
      });
      newData[targetIndex] = row;
    });

    updateData(newData as (Data<T> & Meta)[]);
  }, [data, fields, isCellEditable, resolvePastedValue, updateData]);

  const handlePaste = useCallback((event: React.ClipboardEvent<HTMLDivElement>) => {
    if (!enablePaste) return;

    const text = event.clipboardData?.getData('text/plain');
    if (!text) {
      // O clipboard do sistema pode ficar vazio (permissões, janela sem foco). Havendo uma linha
      // copiada aqui dentro, o Ctrl+V faz o mesmo que o "colar linha" do menu de contexto.
      const anchorRow = findPasteAnchor();
      if (!anchorRow || !rowClipboardRef.current) return;
      event.preventDefault();
      const target = data.findIndex(row => row.__index === anchorRow.row.__index);
      if (target < 0) return;
      const replaced = [...data] as (Data<T> & Partial<Meta>)[];
      replaced[target] = { ...rowClipboardRef.current, __index: data[target].__index };
      updateData(replaced as (Data<T> & Meta)[]);
      return;
    }

    // Um bloco do Excel traz tabulações e/ou mudanças de linha; um valor solto não traz nada disso.
    const isBlock = /[\t\n\r]/.test(text);
    // Com o editOnClick, clicar numa célula abre logo o editor — sem esta distinção o `<input>`
    // ficava com o bloco todo enfiado numa célula só, que era o que estava a acontecer.
    const editorElement = (event.target as HTMLElement)?.closest?.('input, textarea, [contenteditable="true"]');
    if (editorElement && !isBlock) return;

    const matrix = text.replace(/\r\n?/g, '\n').replace(/\n+$/, '').split('\n').map(line => line.split('\t'));
    if (!matrix.length) return;

    const anchor = findPasteAnchor();
    if (!anchor) {
      warningToast(t('spreadsheet_import.validation_step.paste_select_cell_first') as string);
      return;
    }
    event.preventDefault();

    // Fecha o editor descartando o que lá estava: a fechar por si (blur) gravaria o valor antigo
    // por cima da célula que acabámos de colar.
    if (editorElement) {
      editorElement.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }));
    }

    applyPastedText(text, anchor);
  }, [enablePaste, findPasteAnchor, applyPastedText, data, updateData]);

  // Task #66347: o Ctrl+V só chega ao `onPaste` da grelha quando o foco está lá dentro. Depois de
  // clicar num botão da barra, de fechar um menu ou de mexer no filtro, o foco está noutro sítio e a
  // colagem parecia avariada — daí ouvirmos também ao nível do documento. Nunca se rouba o Ctrl+V a
  // quem está a escrever noutro campo nem a um modal aberto por cima.
  const handlePasteRef = useRef(handlePaste);
  handlePasteRef.current = handlePaste;

  useEffect(() => {
    if (!enablePaste) return;

    const onDocumentPaste = (event: ClipboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (gridWrapperRef.current?.contains(target)) return; // já tratado pelo onPaste da grelha
      if (target?.closest?.('input, textarea, [contenteditable="true"]')) return;
      if (document.body.classList.contains('ReactModal__Body--open')) return;
      // Sem nunca se ter clicado numa célula não há onde colar — e um Ctrl+V distraído não pode
      // começar a escrever na grelha por sua conta.
      if (!lastClickedCellRef.current) return;

      handlePasteRef.current(event as unknown as React.ClipboardEvent<HTMLDivElement>);
    };

    document.addEventListener('paste', onDocumentPaste);
    return () => document.removeEventListener('paste', onDocumentPaste);
  }, [enablePaste]);

  const isBlankRow = useCallback((row: Data<T> & Meta) => isRowBlank(row, fields), [fields]);

  // Task #66347: ações de linha do Excel (inserir acima/abaixo, duplicar, cortar/copiar/colar,
  // eliminar) num menu de contexto sobre a grelha. O botão direito é onde as pessoas as procuram.
  const rowClipboardRef = useRef<Data<T> | null>(null);
  const { show: showRowMenu } = useContextMenu({ id: ROW_ACTIONS_MENU_ID });
  const { show: showCopyAllMenu } = useContextMenu({ id: COPY_ALL_MENU_ID });
  const { show: showCellMenu } = useContextMenu({ id: CELL_ACTIONS_MENU_ID });

  const valuesOf = useCallback((row: Data<T> & Meta) => {
    const { __index, __errors, ...values } = row;
    return values as unknown as Data<T>;
  }, []);

  // Task #66347: Ctrl+C sobre a grelha leva para o Excel o que estiver escolhido — linhas marcadas com
  // o visto, senão o bloco de células. O `setData` do próprio evento é síncrono, por isso não depende
  // de permissões de clipboard.
  const handleCopy = useCallback((event: React.ClipboardEvent<HTMLDivElement>) => {
    if (!enablePaste) return;
    // Com um editor aberto quem manda é o comportamento nativo do input.
    if ((event.target as HTMLElement)?.closest?.('input, textarea, [contenteditable="true"]')) return;

    if (!selectedRows.size) {
      const cells = cellRangeToTsv();
      if (cells) {
        event.preventDefault();
        event.clipboardData.setData('text/plain', cells);
        // O que foi copiado não é uma linha: o clipboard interno do "colar linha" deixa de valer.
        rowClipboardRef.current = null;
        return;
      }
    }

    const anchor = selectedRows.size ? null : findPasteAnchor();
    const rows = selectedRows.size
      ? data.filter(row => selectedRows.has(row.__index))
      : (anchor ? [anchor.row] : []);
    if (!rows.length) return;

    event.preventDefault();
    event.clipboardData.setData('text/plain', rowsToTsv(rows, false));

    // Mantém o clipboard interno em sintonia: copiar com Ctrl+C e colar pelo menu de contexto
    // tem de dar a mesma linha.
    if (rows.length === 1) {
      rowClipboardRef.current = valuesOf(rows[0]);
    }
  }, [enablePaste, selectedRows, data, findPasteAnchor, rowsToTsv, valuesOf, cellRangeToTsv]);

  // Ctrl+X: o mesmo âmbito do Ctrl+C. Tem de acompanhá-lo — com o bloco de células escolhido e um
  // Ctrl+X a cortar linhas inteiras, perdiam-se linhas de dados só por se esperar outra coisa.
  const handleCut = useCallback((event: React.ClipboardEvent<HTMLDivElement>) => {
    if (!enablePaste) return;
    if ((event.target as HTMLElement)?.closest?.('input, textarea, [contenteditable="true"]')) return;

    if (!selectedRows.size) {
      const cells = cellRangeToTsv();
      if (cells) {
        event.preventDefault();
        event.clipboardData.setData('text/plain', cells);
        rowClipboardRef.current = null;
        blankCellRange();
        return;
      }
    }

    const anchor = selectedRows.size ? null : findPasteAnchor();
    const rows = selectedRows.size
      ? data.filter(row => selectedRows.has(row.__index))
      : (anchor ? [anchor.row] : []);
    if (!rows.length) return;

    event.preventDefault();
    event.clipboardData.setData('text/plain', rowsToTsv(rows, false));

    if (rows.length === 1) {
      rowClipboardRef.current = valuesOf(rows[0]);
    }

    const cutIds = new Set(rows.map(row => row.__index));
    updateData(data.map(row => (cutIds.has(row.__index)
      ? { ...buildBlankRows(fields, 1)[0], __index: row.__index }
      : row)) as (Data<T> & Meta)[]);
  }, [enablePaste, selectedRows, data, fields, findPasteAnchor, rowsToTsv, valuesOf, updateData, cellRangeToTsv, blankCellRange]);

  const copyToClipboard = useCallback(async (text: string) => {
    const copied = await writeClipboardText(text);
    if (copied) {
      addToast(t('spreadsheet_import.validation_step.copy_done'), { appearance: 'success' });
      return;
    }
    warningToast(t('spreadsheet_import.validation_step.copy_error') as string);
  }, [addToast, warningToast, t]);

  const copyRows = useCallback((rows: (Data<T> & Meta)[], withHeader: boolean) =>
    copyToClipboard(rowsToTsv(rows, withHeader)), [rowsToTsv, copyToClipboard]);

  // Task #66347: o "copiar" do menu da célula — o mesmo que o Ctrl+C, mas partindo de um clique, onde
  // não há evento de clipboard e é preciso pedir a escrita ao browser.
  const copyCellSelection = useCallback(async () => {
    const cells = cellRangeToTsv();
    if (!cells) return;

    rowClipboardRef.current = null;
    await copyToClipboard(cells);
  }, [cellRangeToTsv, copyToClipboard]);

  // O clipboard do sistema pode recusar (sem permissão, janela sem foco). Falhando, o utilizador tem
  // de saber, senão o Ctrl+V a seguir parece avariado. A linha continua no clipboard interno, que o
  // "colar linha" e o próprio Ctrl+V usam como rede.
  const copyRowToClipboard = useCallback(async (row: Data<T> & Meta) => {
    const copied = await writeClipboardText(rowsToTsv([row], false));
    if (!copied) {
      warningToast(t('spreadsheet_import.validation_step.copy_error') as string);
    }
  }, [rowsToTsv, warningToast, t]);

  const indexInData = useCallback((rowIndex: number) => {
    const row = tableDataRef.current[rowIndex];
    return row ? data.findIndex(value => value.__index === row.__index) : -1;
  }, [data]);

  const onGridContextMenu = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    // Task #66347: no cabeçalho o botão direito não é sobre uma linha — é sobre a grelha toda.
    const header = enablePaste ? (event.target as HTMLElement)?.closest?.('[role="columnheader"]') : null;
    if (header) {
      event.preventDefault();
      // A coluna em que se carregou é a que o "colar nesta coluna" vai preencher.
      showCopyAllMenu(event, { props: { columnIndex: Number(header.getAttribute('aria-colindex')) - 1 } });
      return;
    }

    const position = positionOfCell(event.target as HTMLElement);
    if (!position) return;

    // Task #66347: as ações sobre a linha inteira (inserir, duplicar, colar linha, eliminar) só na
    // coluna do "#". Espalhadas por todas as células, uma coluna de custos copiada do Excel era colada
    // com o "colar linha" e os valores caíam a partir da matrícula, por cima do resto da linha.
    if (columns[position.idx]?.key === ROW_NUMBER_COLUMN_KEY) {
      if (!enableRowActions) return;
      event.preventDefault();
      clearCellRange();
      showRowMenu(event, { props: { rowIndex: position.rowIdx } });
      return;
    }

    if (!enablePaste || position.idx < firstDataColumnIndex) return;
    event.preventDefault();

    // O botão direito também escolhe a célula — mas sem desfazer um bloco já marcado que a contenha,
    // que é o caso de quem escolhe cinco custos e vai ao menu copiá-los.
    if (!isInsideBounds(boundsOf(cellRangeRef.current), position)) {
      cellRangeRef.current = { anchor: position, focus: position };
      paintCellRange();
    }

    gridRef.current?.selectCell(position);
    showCellMenu(event, { props: { position } });
  }, [enableRowActions, enablePaste, columns, firstDataColumnIndex, positionOfCell, paintCellRange, clearCellRange, showRowMenu, showCellMenu]);

  // O "colar linha" do menu tem de dar o mesmo que o Ctrl+V: primeiro o clipboard do sistema (é lá
  // que está o que veio do Excel), e só se ele não estiver acessível é que se usa a linha copiada
  // aqui dentro. Sem isto, o menu ignorava tudo o que não tivesse sido copiado na própria grelha.
  const pasteFromClipboard = useCallback(async (targetIndex: number) => {
    const row = data[targetIndex];
    if (!row) return;

    const text = await readClipboardText();
    if (text) {
      applyPastedText(text, { fieldIndex: 0, row });
      return;
    }

    if (rowClipboardRef.current) {
      const replaced = [...data] as (Data<T> & Partial<Meta>)[];
      replaced[targetIndex] = { ...rowClipboardRef.current, __index: row.__index };
      updateData(replaced as (Data<T> & Meta)[]);
      return;
    }

    warningToast(t('spreadsheet_import.validation_step.paste_nothing_to_paste') as string);
  }, [data, applyPastedText, updateData, warningToast, t]);

  // Task #66347: copiar uma coluna do Excel e despejá-la aqui a partir da primeira linha é o gesto que
  // os clientes andavam a tentar fazer célula a célula. O Ctrl+V faz o mesmo, mas obriga a acertar
  // primeiro na célula certa — pelo cabeçalho não há nada para acertar.
  const pasteIntoColumn = useCallback(async (columnIndex: number) => {
    const anchor = anchorFromPosition(0, columnIndex);
    if (!anchor) return;

    const text = await readClipboardText();
    if (!text) {
      // O browser pode recusar a leitura do clipboard (permissão, contexto não seguro). O Ctrl+V
      // continua a funcionar, porque aí o conteúdo vem no próprio evento.
      warningToast(t('spreadsheet_import.validation_step.paste_clipboard_unavailable') as string);
      return;
    }

    applyPastedText(text, anchor);
  }, [anchorFromPosition, applyPastedText, warningToast, t]);

  // O "cortar" do menu da célula: o mesmo que o Ctrl+X — leva o bloco para o clipboard e limpa-o.
  const cutCellSelection = useCallback(async () => {
    const cells = cellRangeToTsv();
    if (!cells) return;

    rowClipboardRef.current = null;
    await copyToClipboard(cells);
    blankCellRange();
  }, [cellRangeToTsv, copyToClipboard, blankCellRange]);

  // Task #66347: o "colar" do menu da célula faz exatamente o que o Ctrl+V faz — começa na célula onde
  // se carregou e espalha dali para a direita e para baixo. É esta a diferença para o "colar linha",
  // que começa sempre na primeira coluna.
  const pasteIntoCell = useCallback(async (position?: CellPosition) => {
    if (!position) return;

    const anchor = anchorFromPosition(position.rowIdx, position.idx);
    if (!anchor) return;

    const text = await readClipboardText();
    if (!text) {
      warningToast(t('spreadsheet_import.validation_step.paste_clipboard_unavailable') as string);
      return;
    }

    applyPastedText(text, anchor);
  }, [anchorFromPosition, applyPastedText, warningToast, t]);

  const runRowAction = useCallback((action: RowAction, rowIndex: number) => {
    const target = indexInData(rowIndex);
    if (target < 0) return;

    const newData = [...data] as (Data<T> & Partial<Meta>)[];
    const blank = () => buildBlankRows(fields, 1)[0];

    switch (action) {
      case 'insertAbove':
        newData.splice(target, 0, blank());
        break;
      case 'insertBelow':
        newData.splice(target + 1, 0, blank());
        break;
      case 'duplicate':
        newData.splice(target + 1, 0, { ...valuesOf(data[target]) });
        break;
      // Copiar e cortar escrevem também no clipboard do sistema: sem isso, copiar pelo menu e colar
      // com Ctrl+V dava o conteúdo anterior do clipboard, e as duas formas de copiar divergiam.
      case 'copy':
        rowClipboardRef.current = valuesOf(data[target]);
        copyRowToClipboard(data[target]);
        return;
      case 'cut':
        rowClipboardRef.current = valuesOf(data[target]);
        copyRowToClipboard(data[target]);
        newData[target] = { ...blank(), __index: data[target].__index };
        break;
      case 'paste':
        // Assíncrono porque tem de ir pedir o clipboard do sistema; sai já para não cair no
        // updateData no fim, que é do caminho síncrono.
        pasteFromClipboard(target);
        return;
      case 'delete':
        newData.splice(target, 1);
        break;
    }

    updateData(newData as (Data<T> & Meta)[]);
  }, [data, fields, indexInData, updateData, valuesOf, copyRowToClipboard, pasteFromClipboard]);

  const submitData = async () => {
    const rowsToSubmit = ignoreBlankRows ? data.filter(row => !isBlankRow(row)) : data;

    const calculatedData = rowsToSubmit.reduce(
      (acc, value) => {
        const { __index, __errors, ...values } = value;
        if (__errors) {
          for (const key in __errors) {
            if (__errors[key].level === 'error') {
              acc.invalidData.push(values as unknown as Data<T>);
              return acc;
            }
          }
        }
        acc.validData.push(values as unknown as Data<T>);
        return acc;
      },
      { validData: [] as Data<T>[], invalidData: [] as Data<T>[], all: data, matchedOptions, matchedColumns, chargeAttachments },
    );

    if (!calculatedData.validData.length) {
      warningToast(t('import_charges.cannot_save_without_valid_lines'));
      return;
    }

    onSubmit(calculatedData, file, () => {
      setShowSubmitAlert(false);
      onClose();
    }, () => {
      //console.log('error')
    });

  };

  const onContinue = () => {

    if (hasAttachmentRequired && !chargeAttachments?.length) {
      warningToast(t('charges.attachment_required'));
      setShowSubmitAlert(true);
      return;
    }

    const errorCount = data.filter(value => {
      if (ignoreBlankRows && isBlankRow(value)) return false;
      if (value?.__errors) {
        return !!Object.values(value.__errors)?.filter(err => err.level === 'error').length;
      }
      return false;
    }).length;
    if (!errorCount) {
      submitData();
    } else {
      warningToast(t('spreadsheet_import.alerts.submit_incomplete.body_text_with_count' as any, { count: errorCount }) as string);
      setFilterByErrors(true);
      setShowSubmitAlert(true);
    }
  };

  //files
  // >>> validação do attachmentRequired
  let hasAttachmentRequired = false;
  const typeCodeField = fields.find(f => f.key === 'typeCode');
  if (typeCodeField?.fieldType.type === 'select') {
    const requiredAttachmentValues = new Set(
      typeCodeField.fieldType.options.filter((opt: any) => opt.attachmentRequired === true)?.map(opt => opt.value)
    );

    hasAttachmentRequired = data.some((row: any) =>
      requiredAttachmentValues.has(row.typeCode)
    );

  }

  const setValuesFiles = (files: CustomFile[]) => {
    const filesAux = files.map(file => {
      (file as any).containerName = 'CHARGE';
      // Garante id único, mesmo que nome seja igual
      if (!file.id) {
        (file as any).id = `${file.name}_${file.lastModified}_${Math.random()}`;
      }
      return file;
    });

    setChargeAttachments(filesAux);

  }

  const onRemoveFile = (file: CustomFile) => {
    if (!file.id) return;
    // Remove do array pelo id
    setChargeAttachments(prev => prev.filter(f => f.id !== file.id));
  }

  return (
    <>
      {tooltipContainerRef.current && createPortal(<ReactTooltip id="validation-unmapped-tooltip" place="top" type="dark" effect="solid" />, tooltipContainerRef.current)}
      {/* Task #66347: em página, a grelha estica até ao fundo em vez de ficar com a altura fixa que o
          react-data-grid usa por omissão — quem faz scroll é a própria grelha. */}
      <div className="spreadsheet-step-scroll" style={asPage
        ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }
        : { flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
      <b>{validationMessage ?? t('spreadsheet_import.validation_step.valid_message')}</b>

      <div style={{ display: 'flex', marginTop: '2rem', marginBottom: '1rem', justifyContent: 'flex-start', alignItems: 'center', gap: '8px' }} >

        <Button preset={'primary'} size={'small'} onlyIcon={true}
          onClick={toggleSelectAllRows}
          style={{ marginLeft: 0 }}
          disabledMessage={t(allRowsSelected
            ? 'spreadsheet_import.validation_step.unselect_all_lines'
            : 'spreadsheet_import.validation_step.select_all_lines') as string}
        >
          <FaCheckDouble />
        </Button>

        <Button preset={'danger'} size={'small'} onlyIcon={true}
          onClick={deleteSelectedRows}
          disabledMessage={t('spreadsheet_import.validation_step.discard_button_title') as string}
        >
          <FaTrashAlt />
        </Button>

        {allowAddRows && <Button preset={'primary'} size={'small'} onlyIcon={true}
          onClick={addBlankRows}
          disabledMessage={t('spreadsheet_import.validation_step.add_rows_button_title', { count: ADD_ROWS_STEP }) as string}
        >
          <FaPlus />
        </Button>}

        {enableHistory && <Button preset={'primary'} size={'small'} onlyIcon={true}
          isDisabled={!undoStack.length}
          onClick={undo}
          disabledMessage={t('spreadsheet_import.validation_step.undo') as string}
        >
          <FaUndo />
        </Button>}

        {enableHistory && <Button preset={'primary'} size={'small'} onlyIcon={true}
          isDisabled={!redoStack.length}
          onClick={redo}
          disabledMessage={t('spreadsheet_import.validation_step.redo') as string}
        >
          <FaRedo />
        </Button>}

        {renderToolbarActions?.({
          rows: data,
          selectedRowIds: Array.from(selectedRows).map(String),
          setRows: rows => updateData(rows as (Data<T> & Meta)[]),
        })}

        {selectedRows.size > 0 && <div
          style={{ display: 'flex', justifyContent: 'flex-end', gap: '16px', alignItems: 'center', flexWrap: 'wrap', marginLeft: 10 }}
        >
          {selectedRows.size == 1 && <label>{t('import_charges.counter_selected_line')} </label>}
          {selectedRows.size > 1 && <label>{t('import_charges.counter_selected_lines', { counter: selectedRows.size })} </label>}
        </div>}

        <div style={{ display: 'flex', flex: 1, justifyContent: 'flex-end', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
          <label
            className={`${toggleSwitchStyle.toggleSwitch} ${toggleSwitchStyle.enabled}`}>
            <input
              type='checkbox'
              checked={filterByErrors}
              onChange={() => {
                if (filterByErrors == false) {
                  setSelectedRows(new Set())
                }
                setFilterByErrors(!filterByErrors);
              }}
            />
            <span className={toggleSwitchStyle.switch}></span>
          </label>
          <label style={{ color: 'rgba(0, 0, 0, 0.6)' }}>{t('spreadsheet_import.validation_step.filter_switch_title')} </label>
        </div>

      </div>

      <div style={asPage ? { flexGrow: 1, minHeight: 0, display: 'flex', flexDirection: 'column' } : { flexGrow: 1 }}
        ref={gridWrapperRef} onPaste={handlePaste} onCopy={handleCopy} onCut={handleCut} onContextMenu={onGridContextMenu}
        onMouseDown={onGridMouseDown} onMouseOver={onGridMouseOver}>
        <Table
          gridRef={gridRef}
          onRowClick={onCellClick}
          style={asPage ? { height: '100%' } : undefined}
          rowKeyGetter={rowKeyGetter}
          onFill={enableFillHandle ? onFill : undefined}
          rowHeight={hasMultiValueColumn ? rowHeight : undefined}
          rows={tableData}
          onRowsChange={updateRow}
          columns={columns}
          rowClass={rowClass}
          selectedRows={selectedRows}
          onSelectedRowsChange={setSelectedRows}

          components={{
            noRowsFallback: (
              <div style={{ display: 'flex', justifyContent: 'center', gridColumnStart: '1', gridColumnEnd: '-1', marginTop: '32px' }}>
                {filterByErrors
                  ? t('spreadsheet_import.validation_step.no_rows_message_when_filtered')
                  : t('spreadsheet_import.validation_step.no_rows_message')}
              </div>
            ),
          }}
        />
      </div>

      {enablePaste &&
        <Menu id={COPY_ALL_MENU_ID} theme={theme.light}>
          <Item onClick={({ props }) => pasteIntoColumn(props?.columnIndex ?? 0)}>
            {t('spreadsheet_import.validation_step.paste_into_column')}
          </Item>
          <Separator />
          <Item onClick={() => copyRows(data.filter(row => !isBlankRow(row)), true)}>
            {t('spreadsheet_import.validation_step.copy_all_with_header')}
          </Item>
          <Item onClick={() => copyRows(data.filter(row => !isBlankRow(row)), false)}>
            {t('spreadsheet_import.validation_step.copy_all')}
          </Item>
        </Menu>}

      {enablePaste &&
        <Menu id={CELL_ACTIONS_MENU_ID} theme={theme.light}>
          <Item onClick={() => copyCellSelection()}>
            {t('spreadsheet_import.validation_step.cell_actions.copy')}
          </Item>
          <Item onClick={() => cutCellSelection()}>
            {t('spreadsheet_import.validation_step.cell_actions.cut')}
          </Item>
          <Item onClick={({ props }) => pasteIntoCell(props?.position)}>
            {t('spreadsheet_import.validation_step.cell_actions.paste')}
          </Item>
        </Menu>}

      {enableRowActions &&
        <Menu id={ROW_ACTIONS_MENU_ID} theme={theme.light}>
          <Item onClick={({ props }) => runRowAction('insertAbove', props.rowIndex)}>
            {t('spreadsheet_import.validation_step.row_actions.insert_above')}
          </Item>
          <Item onClick={({ props }) => runRowAction('insertBelow', props.rowIndex)}>
            {t('spreadsheet_import.validation_step.row_actions.insert_below')}
          </Item>
          <Item onClick={({ props }) => runRowAction('duplicate', props.rowIndex)}>
            {t('spreadsheet_import.validation_step.row_actions.duplicate')}
          </Item>
          <Item onClick={({ props }) => runRowAction('copy', props.rowIndex)}>
            {t('spreadsheet_import.validation_step.row_actions.copy')}
          </Item>
          <Item onClick={({ props }) => runRowAction('cut', props.rowIndex)}>
            {t('spreadsheet_import.validation_step.row_actions.cut')}
          </Item>
          <Item onClick={({ props }) => runRowAction('paste', props.rowIndex)}>
            {t('spreadsheet_import.validation_step.row_actions.paste')}
          </Item>
          <Item onClick={({ props }) => runRowAction('delete', props.rowIndex)}>
            {t('spreadsheet_import.validation_step.row_actions.delete')}
          </Item>
        </Menu>}

      {renderGridFooter && <div style={{ marginTop: '1rem' }}>{renderGridFooter(data)}</div>}

      {showAttachments && <div style={{ marginTop: '2rem' }} >
        <b> {t('spreadsheet_import.validation_step.charges_attachments')}</b>
        <Row style={{ display: 'flex', marginTop: '1rem' }} >
          <Col xl={12}>
            <FileSelector
              isDetails={false}
              isMulti={true}
              initialFiles={chargeAttachments}
              onFilesChanged={setValuesFiles}
              onRemoveFile={onRemoveFile}
              label={<Label>{t('common.add_new_file')} {hasAttachmentRequired ? '*' : ''}</Label>}
              smallButtonAdd={true}
            />
          </Col>
        </Row>
      </div>}

      </div>
      <div style={{ textAlign: 'right', paddingTop: 20, flexShrink: 0 }}>
        <Button preset={'secondary'} type="button" onClick={() => onClose()} text={t('common.cancel')} />
        {(!startWithBlankRows || onBack) &&
          <Button type="button" onClick={() => onPrevious()} text={t('spreadsheet_import.select_header_step.previous_button_title')} />}
        <Button text={submitButtonText ?? t('spreadsheet_import.validation_step.next_button_title')} size={'normal'} onClick={onContinue} />
      </div>

    </>
  );
};
